/**
 * Hook React pour la gestion des sessions
 * RÉUTILISABILITÉ : 100% - Compatible Next.js et React
 *
 * Ce hook encapsule la logique de gestion des sessions :
 * - Liste des sessions actives
 * - Révocation de sessions individuelles
 * - Révocation de toutes les sessions
 * - Informations sur la session actuelle
 */

  // Client Component pour Next.js

import { useEffect } from 'react';
import { useSessionStore } from '../store/sessionStore';
import { useAuthStore } from '../store/authStore';
import type { Session } from '../../application/dto/dtos';

/**
 * Interface du hook useSession
 */
export interface UseSessionReturn {
  // État
  sessions: Session[];
  currentSessionId: string | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchSessions: (actives?: boolean) => Promise<void>;
  revokeSession: (idSession: string) => Promise<void>;
  revokeAllSessions: (motDePasse: string, conserverActuelle?: boolean) => Promise<void>;

  // Utilitaires
  setError: (error: string | null) => void;
  clearError: () => void;
  reset: () => void;

  // Helpers
  getCurrentSession: () => Session | null;
  getOtherSessions: () => Session[];
}

/**
 * Hook pour la gestion des sessions
 *
 * @param autoFetch - Charger automatiquement les sessions au montage (défaut: true)
 *
 * @example
 * ```tsx
 * function SessionsManager() {
 *   const {
 *     sessions,
 *     currentSessionId,
 *     revokeSession,
 *     isLoading
 *   } = useSession();
 *
 *   return (
 *     <div>
 *       {sessions.map((session) => (
 *         <SessionCard
 *           key={session.idSession}
 *           session={session}
 *           isCurrent={session.idSession === currentSessionId}
 *           onRevoke={() => revokeSession(session.idSession)}
 *         />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useSession(autoFetch: boolean = true): UseSessionReturn {
  const sessionStore = useSessionStore();
  const user = useAuthStore((state) => state.user);

  // Charger les sessions au montage
  useEffect(() => {
    if (autoFetch && user?.id) {
      sessionStore.fetchSessions(user.id);
    }
  }, [autoFetch, user?.id]);

  /**
   * Récupérer les sessions
   */
  const fetchSessions = async (actives: boolean = true): Promise<void> => {
    if (!user?.id) {
      sessionStore.setError('Utilisateur non connecté');
      return;
    }

    await sessionStore.fetchSessions(user.id, actives);
  };

  /**
   * Révoquer une session spécifique
   */
  const revokeSession = async (idSession: string): Promise<void> => {
    if (!user?.id) {
      sessionStore.setError('Utilisateur non connecté');
      return;
    }

    await sessionStore.revokeSession(user.id, idSession);
  };

  /**
   * Révoquer toutes les sessions
   */
  const revokeAllSessions = async (
    motDePasse: string,
    conserverActuelle: boolean = true
  ): Promise<void> => {
    if (!user?.id) {
      sessionStore.setError('Utilisateur non connecté');
      return;
    }

    await sessionStore.revokeAllSessions(user.id, motDePasse, conserverActuelle);
  };

  /**
   * Obtenir la session actuelle
   */
  const getCurrentSession = (): Session | null => {
    if (!sessionStore.currentSessionId) return null;
    return sessionStore.sessions.find((s) => s.id === sessionStore.currentSessionId) || null;
  };

  /**
   * Obtenir les autres sessions (non actuelles)
   */
  const getOtherSessions = (): Session[] => {
    return sessionStore.sessions.filter((s) => s.id !== sessionStore.currentSessionId);
  };

  return {
    // État
    sessions: sessionStore.sessions,
    currentSessionId: sessionStore.currentSessionId,
    isLoading: sessionStore.isLoading,
    error: sessionStore.error,

    // Actions
    fetchSessions,
    revokeSession,
    revokeAllSessions,

    // Utilitaires
    setError: sessionStore.setError,
    clearError: sessionStore.clearError,
    reset: sessionStore.reset,

    // Helpers
    getCurrentSession,
    getOtherSessions,
  };
}

/**
 * Hook pour obtenir uniquement le nombre de sessions actives
 * Version optimisée qui ne déclenche pas de re-render inutiles
 *
 * @example
 * ```tsx
 * function SessionCount() {
 *   const count = useSessionCount();
 *
 *   return <Badge>{count} sessions actives</Badge>;
 * }
 * ```
 */
export function useSessionCount(): number {
  return useSessionStore((state) => state.sessions.length);
}

/**
 * Hook pour obtenir uniquement la session actuelle
 * Version optimisée qui ne déclenche pas de re-render inutiles
 *
 * @example
 * ```tsx
 * function CurrentSessionInfo() {
 *   const currentSession = useCurrentSession();
 *
 *   if (!currentSession) return null;
 *
 *   return (
 *     <div>
 *       Appareil actuel : {currentSession.appareil}
 *     </div>
 *   );
 * }
 * ```
 */
export function useCurrentSession(): Session | null {
  const currentSessionId = useSessionStore((state) => state.currentSessionId);
  const sessions = useSessionStore((state) => state.sessions);

  if (!currentSessionId) return null;
  return sessions.find((s) => s.id === currentSessionId) || null;
}

/**
 * Hook pour vérifier si une session est la session actuelle
 *
 * @param sessionId - ID de la session à vérifier
 *
 * @example
 * ```tsx
 * function SessionCard({ session }: { session: Session }) {
 *   const isCurrent = useIsCurrentSession(session.idSession);
 *
 *   return (
 *     <div className={isCurrent ? 'current' : ''}>
 *       {session.appareil}
 *       {isCurrent && <Badge>Actuelle</Badge>}
 *     </div>
 *   );
 * }
 * ```
 */
export function useIsCurrentSession(sessionId: string): boolean {
  const currentSessionId = useSessionStore((state) => state.currentSessionId);
  return currentSessionId === sessionId;
}
